import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Trash2, FileText, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmt12 } from "@/lib/soap";

function Stat({ label, value, id }) {
  return (
    <div className="rounded-xl border bg-white p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-display font-semibold text-slate-900" data-testid={id}>{value}</div>
    </div>
  );
}

function NoteRow({ n, onDelete }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/notes/${n.id}`)}
      data-testid={`note-row-${n.id}`}
      className="group cursor-pointer grid grid-cols-[1fr_auto] gap-4 rounded-xl border bg-white p-5 hover:border-primary/40 hover:shadow-md transition-[box-shadow,border-color]"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display font-semibold text-slate-900">{n.client_name || "Unassigned client"}</span>
          {n.cpt_code && <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-100 text-teal-800 border border-teal-200">{n.cpt_code}</span>}
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${n.status === "final" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {n.status === "final" ? "Final" : "Draft"}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{n.session_date || "No date"}</span>
          {n.setting && <span>{n.setting}</span>}
          {n.start_time && <span>{fmt12(n.start_time)}–{fmt12(n.end_time)}</span>}
        </div>
        <p className="mt-2 text-sm text-slate-600 line-clamp-2">{n.assessment || n.session_details || "No content yet."}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="opacity-60 group-hover:opacity-100 text-slate-500 hover:text-red-600"
        data-testid={`note-delete-${n.id}`}
        onClick={(e) => { e.stopPropagation(); onDelete(n); }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function NotesPage() {
  const [notes, setNotes] = useState(null);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [clientId, setClientId] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/notes", { params: { q, client_id: clientId === "all" ? "" : clientId, status: status === "all" ? "" : status } })
        .then((r) => setNotes(r.data))
        .catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, clientId, status]);

  const remove = async (n) => {
    if (!window.confirm(`Delete note for ${n.client_name || "this client"}?`)) return;
    try { await api.delete(`/notes/${n.id}`); setNotes((ns) => ns.filter((x) => x.id !== n.id)); toast.success("Note deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const list = notes || [];
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Session notes</h1>
          <p className="text-slate-500 mt-1">Every SOAP note you've written, searchable in one place.</p>
        </div>
        <Button asChild className="rounded-full h-11 px-6" data-testid="new-note-button">
          <Link to="/notes/new"><Plus className="h-4 w-4 mr-2" />New SOAP note</Link>
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 stagger">
        <Stat label="Total notes" value={list.length} id="stat-total" />
        <Stat label="Drafts" value={list.filter((n) => n.status === "draft").length} id="stat-drafts" />
        <Stat label="Last 7 days" value={list.filter((n) => n.session_date >= weekAgo).length} id="stat-week" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, CPT code or note text…" className="pl-9 h-11 bg-white" data-testid="note-search-input" />
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="h-11 sm:w-56 bg-white" data-testid="notes-client-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 sm:w-40 bg-white" data-testid="notes-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="final">Final</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {notes && list.length === 0 ? (
        <div className="rounded-2xl border border-dashed bg-white p-12 text-center" data-testid="notes-empty-state">
          <FileText className="h-8 w-8 mx-auto text-primary/60" />
          <h3 className="mt-4 text-lg font-semibold">No notes yet</h3>
          <p className="text-sm text-slate-500 mt-1">Log your session data and let AI draft the SOAP note.</p>
          <Button asChild className="mt-6 rounded-full"><Link to="/notes/new">Write your first note</Link></Button>
        </div>
      ) : (
        <div className="grid gap-3 stagger" data-testid="notes-list">
          {list.map((n) => <NoteRow key={n.id} n={n} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
